// ---------------------------------------------------------------------------
// KG channels renderer.
//
// Renders a synthetic-channels run as:
//   - Summary chips: domain, message count, users, phases, topics, behavioral knobs
//   - Items table:   one row per channel with category / participants / preview
//                    expanded body ? ConversationView (Teams-like bubble list)
//
// Save behavior: one Chat Data entry per channel, each with a
// `<channel>-chat.json` file in the annotation editor's `sessions[]` shape,
// one session per thread (every original KG field is kept on each message).
// ---------------------------------------------------------------------------

import { Body1, Button } from '@fluentui/react-components';
import { useMemo, useState } from 'react';

import {
  ConversationView,
  ExampleTable,
  SummaryChips,
} from '../../viewers/index.js';
import type {
  ConversationBadge,
  ConversationMessage,
  ExampleTableColumn,
  SummaryChip,
} from '../../viewers/index.js';
import type {
  SyntheticDataEntry,
  SyntheticEntryFile,
  SyntheticRendererContext,
  SyntheticResultRenderer,
} from '../renderer.interface.js';

import {
  parseChannels,
  parseMetadata,
  type KgChannel,
  type KgMessage,
} from './KgChannelsRenderer.interface.js';
import { useStyles } from './KgChannelsRenderer.style.js';

const GENERATION_TYPE = 'kgChannels';

// --- blob name detection ---
// The server emits `metadata.json` and `channels_<domain>_<timestamp>.json`
// (and a sibling `.gml`). Identify each role from the result's blob list.
//
// `findMessagesBlob` prefers a positive match on the `channels_*.json`
// naming convention; if nothing matches (older server output, renamed
// files, etc.) it falls back to the first non-metadata JSON so we keep
// rendering something useful.

function findMessagesBlob(
  blobs: readonly string[] | undefined,
): string | undefined {
  if (!blobs) return undefined;
  const positive = blobs.find((n) => {
    const lower = n.toLowerCase();
    return lower.startsWith('channels_') && lower.endsWith('.json');
  });
  if (positive) return positive;
  return blobs.find(
    (n) =>
      n.toLowerCase().endsWith('.json') && n.toLowerCase() !== 'metadata.json',
  );
}

function findMetadataBlob(
  blobs: readonly string[] | undefined,
): string | undefined {
  return blobs?.find((n) => n.toLowerCase() === 'metadata.json');
}

function findGraphBlob(
  blobs: readonly string[] | undefined,
): string | undefined {
  return blobs?.find((n) => n.toLowerCase().endsWith('.gml'));
}

// --- adapters ---
function toConversationMessage(m: KgMessage, i: number): ConversationMessage {
  const badges: ConversationBadge[] = [];
  if (m.phase_name) badges.push({ label: m.phase_name, kind: 'phase' });
  if (m.phase_progress) badges.push({ label: m.phase_progress });
  if (m.stage) badges.push({ label: m.stage });
  if (m.tone) badges.push({ label: m.tone });
  if (m.is_noise) badges.push({ label: '? noise', kind: 'noise' });
  if (m.is_decision_point) {
    badges.push({
      label: `? ${m.decision_type ?? 'decision'}`,
      kind: 'decision',
    });
  }
  return {
    id: m.msg_node ?? `msg-${i}`,
    author: m.author ?? 'unknown',
    role: m.role,
    content: m.content ?? '',
    timestamp: m.timestamp,
    replyTo: m.reply_to ?? null,
    badges,
  };
}

function uniqueAuthors(messages: KgMessage[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const m of messages) {
    const a = m.author;
    if (a && !seen.has(a)) {
      seen.add(a);
      ordered.push(a);
    }
  }
  return ordered;
}

function firstNonEmptyContent(messages: KgMessage[]): string {
  for (const m of messages) {
    if (m.content && m.content.trim().length > 0) return m.content.trim();
  }
  return '';
}

// --- views ---
function buildSummaryChips(
  ctx: SyntheticRendererContext,
  channels: KgChannel[],
): SummaryChip[] {
  const metadataBlob = findMetadataBlob(ctx.result.blobs);
  const meta = parseMetadata(
    metadataBlob ? ctx.blobs[metadataBlob] : undefined,
  );
  const config = ctx.run.config;

  const totalMessages =
    ctx.result.messagesGenerated ??
    channels.reduce((n, c) => n + c.messages.length, 0);

  const chips: SummaryChip[] = [
    { label: 'Examples', value: channels.length },
    { label: 'Messages', value: totalMessages },
  ];
  const domain = meta?.domain ?? config.domain;
  if (domain) chips.push({ label: 'Domain', value: domain });
  if (config.scenario)
    chips.push({ label: 'Scenario', value: truncate(config.scenario, 40) });
  if (meta?.totalPhases ?? config.numPhases) {
    chips.push({
      label: 'Phases',
      value: meta?.totalPhases ?? config.numPhases,
    });
  }
  if (meta?.numTopics ?? config.numTopics) {
    chips.push({ label: 'Topics', value: meta?.numTopics ?? config.numTopics });
  }
  const noise = meta?.noiseRatio ?? config.noiseRatio;
  if (typeof noise === 'number') {
    chips.push({ label: 'Noise', value: `${Math.round(noise * 100)}%` });
  }
  if (meta?.generationTimeSeconds) {
    chips.push({
      label: 'Time',
      value: `${meta.generationTimeSeconds.toFixed(1)}s`,
    });
  }
  return chips;
}

function Summary(ctx: SyntheticRendererContext) {
  const messagesBlob = findMessagesBlob(ctx.result.blobs);
  const channels = useMemo(() => {
    if (!messagesBlob) return [];
    const text = ctx.blobs[messagesBlob];
    if (text === undefined) return [];
    return parseChannels(text) ?? [];
  }, [ctx.blobs, messagesBlob]);

  const chips = useMemo(
    () => buildSummaryChips(ctx, channels),
    [ctx, channels],
  );
  return <SummaryChips items={chips} />;
}

function Items(ctx: SyntheticRendererContext) {
  const styles = useStyles();
  const messagesBlob = findMessagesBlob(ctx.result.blobs);
  const rawText = messagesBlob ? ctx.blobs[messagesBlob] : undefined;
  const channels = useMemo(
    () => (rawText !== undefined ? parseChannels(rawText) : null),
    [rawText],
  );
  const [showRaw, setShowRaw] = useState(false);

  if (rawText === undefined) {
    return <Body1 className={styles.empty}>Loading channel data…</Body1>;
  }
  if (channels === null) {
    // Couldn't parse — fall back to raw JSON so the user still sees something.
    return (
      <>
        <Body1 className={styles.empty}>
          Couldn&apos;t parse the channel data. Showing raw JSON below.
        </Body1>
        <pre className={styles.rawPre}>{rawText}</pre>
      </>
    );
  }
  if (channels.length === 0) {
    return <Body1 className={styles.empty}>No channels were produced.</Body1>;
  }

  const columns: ExampleTableColumn<KgChannel>[] = [
    {
      key: 'name',
      header: 'Channel',
      render: (c) => <span className={styles.channelName}># {c.name}</span>,
      width: '220px',
    },
    {
      key: 'participants',
      header: 'Participants',
      render: (c) => {
        const authors = uniqueAuthors(c.messages);
        if (authors.length <= 3) return authors.join(', ');
        return `${authors.slice(0, 3).join(', ')} (+${authors.length - 3})`;
      },
    },
    {
      key: 'preview',
      header: 'Preview',
      render: (c) => (
        <div className={styles.preview}>{firstNonEmptyContent(c.messages)}</div>
      ),
    },
    {
      key: 'messages',
      header: 'Messages',
      render: (c) => (
        <span className={styles.numCell}>{c.messages.length}</span>
      ),
      width: '90px',
    },
  ];

  return (
    <>
      <ExampleTable
        rows={channels}
        columns={columns}
        rowKey={(c) => c.name}
        expand={(c) => <ChannelConversation channel={c} />}
      />

      <div className={styles.rawToggle}>
        <Button
          appearance="subtle"
          size="small"
          onClick={() => setShowRaw((v) => !v)}
        >
          {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
        </Button>
        {showRaw && <pre className={styles.rawPre}>{rawText}</pre>}
      </div>
    </>
  );
}

// Memoize the per-row message mapping so `ConversationView`'s internal
// `authorColors` memo (keyed on the messages array reference) doesn't bust
// on every row re-render.
function ChannelConversation({ channel }: { channel: KgChannel }) {
  const messages = useMemo(
    () => channel.messages.map((m, i) => toConversationMessage(m, i)),
    [channel.messages],
  );
  return <ConversationView messages={messages} />;
}

// --- save: one Chat Data entry + chat file per channel ---
const SCHEMA_VERSION = 2;
const CHAT_FILE_SUBTYPE = 'chat';

// DataLake rejects these characters in file names.
// eslint-disable-next-line no-control-regex
const INVALID_FILE_NAME_CHARS = /[<>:"/\\|?*\x00-\x1F]/g;

// Root of a message's reply chain; replies to messages outside the channel
// (cross-project replies) start their own thread.
function threadRootId(
  message: KgMessage,
  byId: Map<string, KgMessage>,
  fallbackId: string,
): string {
  const seen = new Set<string>();
  let current = message;
  while (
    current.reply_to &&
    byId.has(current.reply_to) &&
    !seen.has(current.reply_to)
  ) {
    seen.add(current.reply_to);
    current = byId.get(current.reply_to)!;
  }
  return current.msg_node ?? fallbackId;
}

function toChatFile(channel: KgChannel): SyntheticEntryFile {
  const byId = new Map<string, KgMessage>();
  for (const m of channel.messages) {
    if (m.msg_node) byId.set(m.msg_node, m);
  }

  // Workaround: the dataset-entry API and conversation parser have no reply
  // contract yet, so each thread (root post + replies) is written as one
  // `sessions[]` item, which the viewer's session selector can navigate.
  const threads = new Map<string, KgMessage[]>();
  channel.messages.forEach((m, i) => {
    const rootId = threadRootId(m, byId, `msg-${i}`);
    const thread = threads.get(rootId) ?? [];
    thread.push(m);
    threads.set(rootId, thread);
  });

  const sessions = [...threads].map(([rootId, messages]) => {
    const { startTime, endTime } = timeRange(messages);
    return {
      session_id: rootId,
      start_ts: startTime,
      end_ts: endTime,
      chat_messages: messages.map((m) => ({
        ts: m.timestamp,
        sender: m.author,
        text: m.content,
        ...m,
      })),
    };
  });

  return {
    fileName: `${channel.name.replace(INVALID_FILE_NAME_CHARS, '_')}-chat.json`,
    subType: CHAT_FILE_SUBTYPE,
    content: JSON.stringify(
      { id: channel.name, channel_name: channel.name, sessions },
      null,
      2,
    ),
  };
}

function timeRange(messages: KgMessage[]): {
  startTime?: string;
  endTime?: string;
} {
  let start: { ms: number; raw: string } | undefined;
  let end: { ms: number; raw: string } | undefined;
  for (const m of messages) {
    const ms = m.timestamp ? Date.parse(m.timestamp) : NaN;
    if (!m.timestamp || Number.isNaN(ms)) continue;
    if (!start || ms < start.ms) start = { ms, raw: m.timestamp };
    if (!end || ms > end.ms) end = { ms, raw: m.timestamp };
  }
  return { startTime: start?.raw, endTime: end?.raw };
}

function toDataEntries(ctx: SyntheticRendererContext): SyntheticDataEntry[] {
  const messagesBlob = findMessagesBlob(ctx.result.blobs);
  if (!messagesBlob) {
    throw new Error('No messages blob in result.');
  }
  const text = ctx.blobs[messagesBlob];
  if (text === undefined) {
    throw new Error(`Result file '${messagesBlob}' not loaded yet.`);
  }
  const channels = parseChannels(text);
  if (channels === null || channels.length === 0) {
    // Couldn't parse — keep the raw output as a file so the result isn't lost.
    // DataLake requires every entry to match the dataset's data type.
    return [
      {
        dataType: 'Chat Data',
        name: messagesBlob,
        properties: { schemaVersion: SCHEMA_VERSION },
        files: [{ fileName: messagesBlob, content: text }],
      },
    ];
  }
  return channels.map((c) => ({
    dataType: 'Chat Data',
    subType: 'channel',
    name: c.name,
    channelId: c.name,
    ...timeRange(c.messages),
    properties: {
      schemaVersion: SCHEMA_VERSION,
      channelName: c.name,
      messageCount: c.messages.length,
    },
    files: [toChatFile(c)],
  }));
}

// --- public renderer ---
export const kgChannelsRenderer: SyntheticResultRenderer = {
  generationType: GENERATION_TYPE,
  displayName: 'Knowledge Graph',
  datasetDataType: 'Chat Data',
  selectBlobsToLoad: (result) => {
    const wanted: string[] = [];
    const messages = findMessagesBlob(result.blobs);
    const metadata = findMetadataBlob(result.blobs);
    if (messages) wanted.push(messages);
    if (metadata) wanted.push(metadata);
    return wanted;
  },
  Summary,
  Items,
  toDataEntries,
  metadataBlobs: (result) => {
    const graph = findGraphBlob(result.blobs);
    return graph ? [graph] : [];
  },
};

// --- utils ---
function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}
